import React from 'react';
import { Text } from '@/shared/ui';
import {
  CalendarIcon,
  ClockIcon,
  PersonIcon,
} from '@/shared/ui/radix-icons';
import { BuildingIcon } from '@/shared/ui/components/basic/icons';
import type { Visit } from '@services/requests/housien-models/Visit';
import { buildGoogleMapsLink } from '@/shared/utils/maps-link';
import { formatLocalizedDate } from '@/shared/utils/date';
import type { VisitUrgency } from '@/shared/utils/relative-visit-time';

export interface MetaChip {
  icon: React.ReactNode;
  label: string;
}

/**
 * Builds meta chips from visit data (date, time, broker, property).
 *
 * @param visit - The visit being displayed.
 * @param options - Set `showBroker: false` for solo-broker agencies, where the
 *   single user is always the broker and the chip carries no information.
 */
export function buildMetaChips(visit: Visit, options: { showBroker?: boolean } = {}): MetaChip[] {
  const { showBroker = true } = options;
  const chips: MetaChip[] = [];
  const datetime = visit.visit_datetime ?? '';
  const dateStr = datetime.slice(0, 10);
  const timeStr = datetime.slice(11, 16);
  if (dateStr) {
    chips.push({ icon: <CalendarIcon width={12} height={12} />, label: formatDate(dateStr) });
  }
  if (timeStr) {
    chips.push({ icon: <ClockIcon width={12} height={12} />, label: timeStr });
  }
  const broker = showBroker ? visit.broker_name ?? visit.owner_email : '';
  if (broker) {
    chips.push({ icon: <PersonIcon width={12} height={12} />, label: broker });
  }
  const propLabel = resolvePropertyChipLabel(visit);
  if (propLabel) {
    chips.push({ icon: <BuildingIcon width={12} height={12} />, label: propLabel });
  }
  return chips;
}

/**
 * Resolves a short property label for the meta chip row.
 */
function resolvePropertyChipLabel(visit: Visit): string {
  const prop = visit.properties?.[0];
  if (!prop) return '';
  const loc = (prop as Record<string, unknown>).location as Record<string, string> | undefined;
  const short = [loc?.street, loc?.number].filter(Boolean).join(', ');
  if (short) return short;
  const desc = (prop as Record<string, unknown>).description as Record<string, string> | undefined;
  return desc?.headline ?? '';
}

/**
 * Builds the subtitle from property address info.
 */
export function buildSubtitle(visit: Visit): string {
  const prop = visit.properties?.[0];
  if (!prop) return '';
  const loc = (prop as Record<string, unknown>).location as Record<string, string> | undefined;
  if (!loc) return '';
  const parts = [loc.street, loc.number, loc.neighborhood, loc.city].filter(Boolean);
  return parts.join(', ');
}

/**
 * Formats YYYY-MM-DD into a localized date string.
 *
 * Pinned to UTC on purpose: visit datetimes carry a wall-clock time with a
 * nominal `Z`, and the rest of the feature reads them back with `getUTC*`
 * (see `parseBrtDateTime`). Formatting in the local zone would parse the
 * date-only slice as UTC midnight and render the previous day in BRT.
 */
function formatDate(iso: string): string {
  if (!iso) return '—';
  return formatLocalizedDate(iso, undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }) || '—';
}

/**
 * Builds a Google Maps link from the first property in a visit.
 */
export function buildMapsLinkFromVisit(visit: Visit): string {
  const prop = visit.properties?.[0];
  if (!prop) return '';
  const loc = (prop as Record<string, unknown>).location as Record<string, string> | undefined;
  if (!loc) return '';
  const parts = [loc.street, loc.number, loc.neighborhood, loc.city, loc.state].filter(Boolean);
  return buildGoogleMapsLink(parts.join(', '));
}

/**
 * Avatar circle displaying the client's initials.
 */
export function ClientInitials({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '??';

  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-(--seed-base) text-sm font-bold text-[var(--text-inverse)]">
      {initials}
    </div>
  );
}

const URGENCY_STYLES: Record<VisitUrgency, string> = {
  imminent: 'text-[var(--seed-base)] font-bold',
  today: 'text-[var(--text-primary)] font-semibold',
  upcoming: 'text-[var(--text-secondary)]',
  past: 'text-[var(--text-tertiary)]',
};

interface RelativeTimeBadgeProps {
  label: string;
  urgency: VisitUrgency;
}

/**
 * Inline badge showing relative time with urgency-appropriate styling.
 */
export function RelativeTimeBadge({ label, urgency }: RelativeTimeBadgeProps) {
  return (
    <Text
      component="span"
      data-testid="visit-relative-time"
      className={`inline-flex items-center gap-(--space-1) text-[length:var(--text-meta)] ${URGENCY_STYLES[urgency]}`}
    >
      {urgency === 'imminent' && (
        <Text component="span" className="w-2 h-2 rounded-full bg-[var(--seed-base)] animate-pulse" />
      )}
      {label}
    </Text>
  );
}
